import { z } from "zod";

import { TokenExchangeMethodSchema } from "../../../../../schemas/AgentExternalTool/auth/CustomManifest/OAuth.schema";
import {
  optionalSafeExternalUrlSchema,
  safeExternalUrlSchema
} from "../../../../../shared/network/safe-external-url.schema";

// ============================================================================
// EXTERNAL TOOL (CUSTOM_MANIFEST)
// ============================================================================

export const ExternalToolIdParamSchema = z.object({
  // Enforce UUID at the seam so the adapter's `${id}::uuid` cast never
  // sees a malformed value — bad input fails with a clean 400 instead
  // of leaking a Postgres invalid-syntax error as a 500.
  externalToolId: z.string().uuid()
});

export const ExternalToolAuthSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({
    type: z.literal("service_http"),
    apiKey: z.string().min(1, "apiKey is required for service_http"),
    authorization_type: z.enum(["basic", "bearer", "custom"]),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({
    type: z.literal("user_http"),
    authorization_type: z.enum(["basic", "bearer", "custom"]),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({
    type: z.literal("oauth"),
    grant_type: z.string(),
    client_id: z.string(),
    client_secret: z.string(),
    // Both are token endpoints this backend POSTs `client_secret` and a
    // decrypted `refresh_token` to (NEX-3519 item 6).
    client_url: optionalSafeExternalUrlSchema,
    authorization_url: optionalSafeExternalUrlSchema,
    scope: z.string().optional(),
    // One list, owned by the manifest schema: a value the token exchange cannot
    // perform is refused here instead of failing after the user has already been
    // sent to the provider and back.
    tokenExchangeMethod: TokenExchangeMethodSchema.optional()
  }),
  z.object({
    type: z.literal("user_oauth"),
    grant_type: z.string(),
    authorization_type: z.enum(["basic", "bearer", "custom"]).optional(),
    custom_header_name: z.string().optional(),
    custom_header_prefix: z.string().optional()
  }),
  z.object({ type: z.literal("keys") })
]);

// Custom HTTP headers (header-name → value) applied to every action of the
// tool, independent of auth — primarily a browser-like User-Agent to bypass
// anti-bot WAF 403s (Workday/Cloudflare/Akamai). See NEX-2017.
export const ExternalToolCustomHeadersSchema = z.record(z.string().min(1), z.string());

export const CreateExternalToolBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().optional(),
  imageUrl: z.string().url("imageUrl must be a valid URL").optional(),
  documentation: z.string().optional(),
  openApiSpec: z.string().min(1, "OpenAPI spec is required"),
  // TENANT-WRITTEN, and the backend later dials it with the tool's DECRYPTED
  // credentials attached. Same guard as `CustomModel.baseUrl` — see
  // `safe-external-url.schema.ts` (NEX-3519 item 5).
  endpointUrl: safeExternalUrlSchema,
  auth: ExternalToolAuthSchema,
  customHeaders: ExternalToolCustomHeadersSchema.optional()
});

export const UpdateExternalToolBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  documentation: z.string().nullable().optional(),
  openApiSpec: z.string().min(1).optional(),
  endpointUrl: optionalSafeExternalUrlSchema.optional(),
  auth: ExternalToolAuthSchema.optional(),
  // Pass `{}` to clear all custom headers, or omit to leave unchanged.
  customHeaders: ExternalToolCustomHeadersSchema.optional()
});

export const DeleteExternalToolParamsSchema = z.object({
  // Strict literal "true" or omitted. Any other value (e.g. "false", "1") is
  // rejected with 400 — the v1 controller parses against this schema and
  // throws BadRequestException on failure. `z.coerce.boolean()` would wrongly
  // treat "false" as truthy, hence the literal.
  force: z.literal("true").optional()
});

// Query params for PATCH /skills/external-tools/:id. `force=true` skips the
// breaking-change guard so a spec refresh may drop/rename an action key that
// downstream wiring (workflow plugin nodes, agent tool configs) still binds.
// Same strict-literal rule as the delete guard above.
export const UpdateExternalToolParamsSchema = z.object({
  force: z.literal("true").optional()
});

export const DeleteExternalToolResponseSchema = z.object({
  deleted: z.literal(true)
});

// Shape of the structured `details` returned by DELETE /skills/external-tools/:id
// when the tool has attached AgentToolConfigs (HTTP 409, code TOOL_HAS_ATTACHMENTS).
//
// This schema is the SINGLE definition of the shape. The producer —
// `ToolHasAttachmentsErrorDetails` in apps/backend's external-tool.errors.ts —
// is a `z.infer` alias of it, so a field added here changes what is actually
// sent. The CLI cannot safeParse (importing Zod is the +5MB its hand-declared
// wire types exist to avoid); its copy in packages/cli/src/external-tool-wire-types.ts
// is compared against this schema by external-tool-wire-types.conformance.ts,
// so a change here reaches it as a typecheck failure.
export const ToolHasAttachmentsDetailsSchema = z.object({
  total: z.number().int().nonnegative(),
  sample: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      agentId: z.string(),
      agentName: z.string()
    })
  )
});

/**
 * The inferred shape, exported so consumers that cannot import Zod can still
 * name it. `z.infer` inside a package with no `zod` dependency resolves to
 * `any`, and an `any` on one side of a structural comparison satisfies every
 * assertion — so a gate written that way passes while comparing nothing. The
 * CLI is exactly that consumer.
 */
export type TToolHasAttachmentsDetails = z.infer<typeof ToolHasAttachmentsDetailsSchema>;

// Shape of the structured `details` returned by PATCH /skills/external-tools/:id
// when refreshing `openApiSpec` would drop/rename an action key still bound by
// downstream wiring (HTTP 409, code TOOL_SPEC_BREAKING_CHANGE). `removedActions`
// lists the dropped operationIds that are still bound downstream (the breaking
// subset, so its count lines up with the bindings); `bindings` samples the
// workflow nodes / agent tool configs that reference them, and `total` is the
// full count of such bindings.
//
// Same single-definition arrangement as ToolHasAttachmentsDetailsSchema above:
// the backend's ToolSpecBreakingChangeErrorDetails infers from this, and the
// CLI's copy is gated against it.
export const ToolSpecBreakingChangeDetailsSchema = z.object({
  removedActions: z.array(z.string()),
  total: z.number().int().nonnegative(),
  bindings: z.array(
    z.object({
      kind: z.enum(["workflow", "agent"]),
      id: z.string(),
      label: z.string(),
      action: z.string()
    })
  )
});

/** See {@link TToolHasAttachmentsDetails} for why the inferred type is exported. */
export type TToolSpecBreakingChangeDetails = z.infer<typeof ToolSpecBreakingChangeDetailsSchema>;

export const ExternalToolDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  documentation: z.string().nullable(),
  type: z.literal("CUSTOM_MANIFEST"),
  endpointUrl: z.string().nullable(),
  status: z.string(),
  actionsCount: z.number(),
  authType: z.string(),
  createdAt: z.string()
});

export const ListExternalToolsResponseSchema = z.object({
  items: z.array(ExternalToolDetailSchema),
  total: z.number()
});

// ============================================================================
// TEST EXTERNAL TOOL
// ============================================================================

export const TestExternalToolBodySchema = z.object({
  operationId: z.string().min(1, "operationId is required"),
  input: z.record(z.string(), z.unknown()),
  // `ToolCredentials.id` is `@db.Uuid`, matching the uuid-checked `toolCredentialId`
  // on `AgentToolConfigSchema`.
  toolCredentialId: z.string().uuid().optional()
});

export const TestExternalToolResponseSchema = z.object({
  status: z.enum(["success", "error"]),
  output: z.unknown(),
  error: z.string().optional(),
  executionTimeMs: z.number()
});
